'use client';

import { Button } from '@ironhack/design-system2/components/button';
import { type FlexProps } from '@ironhack/design-system2/components/flex';
import {
  LinkBox,
  LinkOverlay,
} from '@ironhack/design-system2/components/link-box';
import { SuperCard } from '@ironhack/design-system2/components/supercard';
import { render } from 'datocms-structured-text-to-plain-text';
import NextLink from 'next/link';
import { take } from 'ramda';
import { type ReactElement } from 'react';

import { CmsImage } from '@/app/components/server/cms-image';
import { getPageUrl } from '@/app/urls/url-mapping';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { type DatoListArticle } from '@/lib/datocms/types';
import { type GtmEvent, sendEvent } from '@/lib/gtm';

type Size = 'l' | 'xl' | '2xl' | '3xl';

export type ArticleCardData = {
  buttonText: string;
  gaEvent?: GtmEvent;
  size: Size | Array<Size | null>;
} & DatoListArticle;

type Props = {
  card: ArticleCardData;
} & FlexProps;

export const ArticleCard = (props: Props): ReactElement => {
  const { card, ...flexProps } = props;
  const {
    author,
    body,
    buttonText,
    coverImage,
    gaEvent,
    publicationDate,
    readingTime,
    series,
    size,
    slug,
    subtitle,
    title,
  } = card;
  const {
    params: { language, region },
  } = usePageContext();

  const articleUrl = getPageUrl('blogArticle', { language, region, slug });
  const onClick = gaEvent && { onClick: () => sendEvent(gaEvent) };

  return (
    <SuperCard as={LinkBox} height="full" size={size} {...flexProps}>
      <SuperCard.Header>
        <SuperCard.Media>
          {coverImage && (
            <CmsImage
              data={coverImage.responsiveImage}
              objectFit="cover"
              objectPosition="left"
              style={{ height: '100%' }}
            />
          )}
        </SuperCard.Media>
        <SuperCard.MediaBadges
          {...(series && { primaryTag: { text: series.name } })}
          {...(readingTime && { secondaryTag: { text: readingTime } })}
        />
      </SuperCard.Header>
      <SuperCard.Body flexGrow={1}>
        <LinkOverlay as={NextLink} href={articleUrl} {...onClick}>
          <SuperCard.Title as="h3" noOfLines={3} textStyle="xlBold">
            {title}
          </SuperCard.Title>
        </LinkOverlay>
        <SuperCard.Note>{`${author.name} - ${publicationDate}`}</SuperCard.Note>
        <SuperCard.Subtitle noOfLines={3} textStyle="m">
          {/* subtitle is optional so fallback to a short version of body */}
          {subtitle || `${take(200, render(body) ?? '')}…`}
        </SuperCard.Subtitle>
      </SuperCard.Body>
      <SuperCard.Actions>
        <Button
          as={NextLink}
          href={articleUrl ?? '#/'}
          role="presentation"
          size="s"
          variant="secondary"
          w="full"
          {...onClick}
        >
          {buttonText}
        </Button>
      </SuperCard.Actions>
    </SuperCard>
  );
};
